import { useEffect, useState } from 'react';
import type { Session } from '../../utils/auth';
import { TokenRefreshUnavailableError, useAuthStore } from '../../utils/auth';
import currentSession from '../../utils/currentSession';

export interface SessionGate {
  isChecking: boolean;
  hasSession: boolean;
}

// Decides which route tree the app mounts. The stored session answers that on
// its own, so the answer moves in the same render the store does: sign-in
// routes to `/` in the microtask chain that stores the session, and a gate
// waiting on its own re-check would leave the public Landing page up over a
// valid session until that check resolved.
//
// The check exists for the token behind the session. `ensureValidToken`
// refreshes an expired one, and clears the store itself when the refresh comes
// back rejected, which moves `hasSession` without help. `rejected` covers what
// that leaves: a check that fails while the store still holds the session it
// failed for. Holding the session rather than a flag is what lets a later
// sign-in retire the verdict during render.
export function useSessionGate(): SessionGate {
  const session = useAuthStore((s) => s.session);
  const [isChecking, setIsChecking] = useState(true);
  const [rejected, setRejected] = useState<Session | null>(null);

  useEffect(() => {
    let mounted = true;
    async function checkSession() {
      try {
        const { jwtToken } = await currentSession();
        if (mounted && !jwtToken) setRejected(session);
      } catch (err) {
        // A refresh Cognito never answered leaves the stored session in place,
        // so the user keeps the app instead of being dropped onto the public
        // routes. Requests fail while the network is down; the session is not
        // over. `ensureValidToken` only throws this once it has a stored
        // session with both an id token and a refresh token, so the error
        // itself is proof there is a session to keep.
        if (mounted && !(err instanceof TokenRefreshUnavailableError)) {
          setRejected(session);
        }
      } finally {
        // Never set back to true. The first check is the one that has to
        // finish before either tree mounts: a session restored from
        // localStorage can carry a token that no longer refreshes, and until
        // this runs nothing has asked Cognito. Later checks run against a
        // session the store already answered for, and re-gating on them would
        // drop the loading overlay over a working app every time a silent
        // refresh replaced the session.
        if (mounted) setIsChecking(false);
      }
    }
    checkSession();
    return () => {
      mounted = false;
    };
  }, [session]);

  return { isChecking, hasSession: !!session && session !== rejected };
}
